/**
 * Finance V2 shared bits — category chips, KPI cards, money helpers.
 * Named exports; single file to keep the surface small.
 */
import React from "react";
import { AlertTriangle } from "lucide-react";

export const fmtMoney0 = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
export const fmtMoney2 = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);
export const fmtPct = (n, digits = 2) => n == null ? "—" : `${(n * 100).toFixed(digits)}%`;
export const fmtNum = (n) => n == null ? "—" : new Intl.NumberFormat("en-US").format(n);

export const CATEGORY = {
  contractual: { bg: "#EFF6FF", border: "#BFDBFE", label: "Contractual", color: "#1E40AF" },
  realized:    { bg: "#ECFDF5", border: "#A7F3D0", label: "Realized",    color: "#065F46" },
  forecast:    { bg: "#FEF3C7", border: "#FCD34D", label: "Forecast",    color: "#92400E" },
  mixed:       { bg: "#F3F4F6", border: "#E5E7EB", label: "Mixed",       color: "#374151" },
  not_configured: { bg: "#F3F4F6", border: "#D1D5DB", label: "Not Configured", color: "#6B7280" },
};

export const CategoryChip = ({ category }) => {
  const m = CATEGORY[category] || CATEGORY.contractual;
  return (
    <span className="inline-block text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
          style={{ color: m.color, backgroundColor: m.bg, border: `1px solid ${m.border}` }}
          data-testid={`category-chip-${category}`}>
      {m.label}
    </span>
  );
};

export const Section = ({ title, category, subtitle, children }) => {
  const meta = CATEGORY[category] || CATEGORY.mixed;
  return (
    <section className="mb-8">
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="font-display text-xl font-semibold text-[#0A2540]">{title}</h2>
        {category && <CategoryChip category={category} />}
      </div>
      {subtitle && <div className="text-xs text-[#6B7280] mb-3">{subtitle}</div>}
      <div className="rounded-lg border p-5" style={{ borderColor: meta.border, backgroundColor: meta.bg + "40" }}>
        {children}
      </div>
    </section>
  );
};

export const Kpi = ({ label, value, sub, accent, negative, testid }) => (
  <div className="rounded-md border border-[#E5E7EB] bg-white p-3.5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5">{label}</div>
    <div className={`text-2xl font-semibold tabular-nums ${accent ? "text-[#0066FF]" : negative ? "text-[#991B1B]" : "text-[#0A2540]"}`}>{value}</div>
    {sub && <div className="text-[10px] text-[#9CA3AF] mt-1">{sub}</div>}
  </div>
);

export const AsOfPicker = ({ value, onChange, testid = "finance-as-of" }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">As of</label>
    <input type="date" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
           className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white" />
  </div>
);

export const EmptyRealizedNotice = ({ cnt }) => (
  <div className="rounded-md bg-[#FFFBEB] border border-[#FCD34D] p-4 mb-4 text-xs text-[#78350F] flex items-start gap-2" data-testid="realized-empty-notice">
    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <div>
      <div className="font-medium">Realized totals are 0 by design.</div>
      <div className="mt-1">Every closed loan is either missing a recorded amount or flagged complex. Enter settlement amounts through the existing loan-detail workflow to populate this section — no data migration required.</div>
    </div>
  </div>
);

export const todayIso = () => new Date().toISOString().slice(0, 10);


export const NotConfiguredNotice = ({ ctaText = "Configure a Capital Structure", ctaHref = "/admin/finance/capital-structures" }) => (
  <div className="rounded-md bg-[#F9FAFB] border border-[#D1D5DB] p-4 mb-4 text-xs text-[#374151] flex items-start gap-2" data-testid="not-configured-notice">
    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#6B7280]" />
    <div className="flex-1">
      <div className="font-medium text-[#0A2540]">No Capital Structure configured.</div>
      <div className="mt-1">
        Cost-of-capital and Net Portfolio Contribution require an explicitly configured
        Capital Structure. Finance V2 does NOT assume a default cost of capital.
      </div>
      <a href={ctaHref} className="inline-block mt-2 text-[#0066FF] hover:underline" data-testid="configure-cta">
        → {ctaText}
      </a>
    </div>
  </div>
);
